import React, { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { FaInfoCircle, FaVideo, FaShoppingCart } from "react-icons/fa";
import { FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import { motion } from "framer-motion";
import { useUser, useAuth, useClerk } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import "./CreateImage.css";
import ErrorAlert from "../components/ErrorAlert";
import GeneratedVideo from '../components/GeneratedVideo';
import CreateVideoEditor from '../components/CreateVideoEditor';
import { useUserDetails } from '../context/UserContext';
import ContentSkeleton from '../components/ContentSkeleton';
import FirstTimeUserTour from '../components/FirstTimeUserTour';
import { PAGE_TYPES, getSessionKey, setSessionData, getSessionData, removeSessionData } from '../utils/sessionStorageHelper';
import { buildFeatureGates, applyAutoDowngrade } from '../utils/featureGates';

/*
  CreateVideo Component

  This screen provides an interface for users to generate custom content.
  It includes optional select boxes for menu items, events, and tone,
  a textarea for input (with an AI Magic feature), multiple image uploads
  with thumbnail preview, and conditional AI image settings.
  Once the user clicks "Generate Content" (or "Regenerate"),
  the generated content appears with an image slider for the generated images
  and the generated text is shown below.
  
  UI-UX guidelines:
    - Minimalistic, sleek and clean interface.
    - Small info icons placed next to each input that show a tooltip when hovered.
    - Refresh icons trigger simulated backend calls for updating generated content.
    - When regenerating, display a spinner overlay on the generated image and disable the button.
*/

// Custom Header Component for CreateVideo - MOVED OUTSIDE to prevent re-renders
const CreateContentHeader = React.memo(({ creditInfo, onHelpClick, hasApiCredits }) => {
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { userDetails } = useUserDetails();
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProfileClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleUserProfileClick = () => {
    window.open(process.env.REACT_APP_USER_PROFILE, '_blank');
    setIsDropdownOpen(false);
  };

  const handleLogout = () => {
    signOut();
    setIsDropdownOpen(false);
  };


  return (
      <header className="fixed top-0 left-0 right-0 z-50 w-full">
      {/* Glass Header Container */}
      <div className="glass-effect border-b border-white/5 backdrop-blur-2xl bg-black/10">
        <div className="max-w-7xl mx-auto px-2 sm:px-3 md:px-4 lg:px-6 py-1">
          <div className="relative flex items-center justify-between">
            
            {/* Left Section - Logo and Taco */}
            {!isSignedIn && (
              <div className="flex items-center gap-1 lg:gap-1.5">
                {/* Logo */}
                <img 
                  src="/logo.png" 
                  alt="Taco Logo" 
                  className="w-5 h-5 sm:w-6 sm:h-6"
                />
                
                {/* Taco Text */}
                <h1 className="text-sm sm:text-base lg:text-lg font-bold">
                  <span className="gradient-text">Taco</span>
                </h1>
              </div>
            )}

            {/* Centered Title - Desktop */}
            <div className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 hidden sm:flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-lg bg-white/5 border border-white/5 flex items-center justify-center">
                <FaVideo className="text-white/70 text-sm" />
              </div>
              <div className="text-white/80 text-sm font-medium tracking-wide">Apparel Videos</div>
            </div>

            {/* Mobile Title - Positioned to avoid sidebar conflicts */}
            <div className="sm:hidden flex items-center gap-1.5 ml-12">
              <div className="w-6 h-6 rounded-lg bg-white/5 border border-white/5 flex items-center justify-center">
                <FaVideo className="text-white/70 text-xs" />
              </div>
              <div className="text-white/80 text-sm font-medium tracking-wide">Apparel Videos</div>
            </div>

            {/* Right Section - Auth Buttons or Profile */}
            <div className="flex items-center gap-1 sm:gap-1.5 ml-auto">
              {isLoaded && (
                <>
                  {isSignedIn ? (
                    /* Profile Section with Dropdown - When Signed In */
                    <div className="relative" ref={dropdownRef}>
                      <div 
                        onClick={handleProfileClick}
                        className="flex items-center gap-1 sm:gap-1.5 cursor-pointer transition-all duration-300 group transition-all duration-300 px-1 sm:px-1.5 md:px-2 py-0.5 rounded-full hover:bg-white/10"
                      >
                        {/* User Avatar */}
                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full overflow-hidden border border-gray-300 group-hover:border-white transition-colors duration-300">
                          {user?.imageUrl ? (
                            <img 
                              src={user.imageUrl} 
                              alt="Profile" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                              </svg>
                            </div>
                          )}
                        </div>
                        
                        {/* User Name and Plan Info */}
                        <div className="flex flex-col items-start">
                          <span className="text-xs font-medium text-gray-200 group-hover:text-white transition-colors duration-300 leading-tight">
                            {user?.firstName} {user?.lastName}
                          </span>
                          {/* Plan info - more compact on mobile */}
                          <div className="flex items-center gap-0.5 sm:gap-1 mt-0 leading-tight">
                            {/* <span className="text-xs text-green-400 font-medium">
                              {(userDetails?.usage?.account_type || 'Trial').replace(/\b\w/g, l => l.toUpperCase())}
                            </span> */}
                            {/* <span className="text-xs text-gray-400">•</span> */}
                            <span className={`text-xs font-medium ${parseFloat(creditInfo?.credit_left_amt || userDetails?.credit_left_amt || '0') > 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {parseFloat(hasApiCredits ? (creditInfo?.credit_left_amt ?? '0') : (userDetails?.credit_left_amt ?? '0')).toFixed(2)} credits
                            </span>
                          </div>
                        </div>
                        
                        {/* Dropdown Arrow */}
                        <svg className={`w-3 h-3 text-gray-400 group-hover:text-gray-200 transition-all duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>

                      {/* Dropdown Menu */}
                      {isDropdownOpen && (
                        <div className="absolute right-0 top-full mt-2 w-64 sm:w-72 bg-gray-900/95 border border-gray-700/50 rounded-xl shadow-2xl z-50 overflow-hidden backdrop-blur-xl">
                          {/* User Info Header */}
                          <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-700/50">
                            <div className="flex items-center gap-2 sm:gap-3">
                              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden border-2 border-gray-600">
                                {user?.imageUrl ? (
                                  <img 
                                    src={user.imageUrl} 
                                    alt="Profile" 
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                    </svg>
                                  </div>
                                )}
                              </div>
                              <div>
                                <p className="text-white font-medium text-sm">
                                  {user?.firstName} {user?.lastName}
                                </p>
                                <p className="text-gray-400 text-xs">
                                  {user?.primaryEmailAddress?.emailAddress}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="py-1 sm:py-2">
                            <button
                              onClick={() => {
                                navigate('/billing');
                                setIsDropdownOpen(false);
                              }}
                              className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors duration-300 flex items-center gap-2 text-xs sm:text-sm"
                            >
                              <FaShoppingCart className="text-gray-300" />
                              Buy More Credits
                            </button>
                            <button
                              onClick={() => {
                                window.open('mailto:manesh.pai@visualtaco.com?subject=Hi there!', '_blank');
                                setIsDropdownOpen(false);
                              }}
                              className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors duration-300 flex items-center gap-2 text-xs sm:text-sm"
                            >
                              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              Contact Us
                            </button>
                            <button
                              onClick={handleUserProfileClick}
                              className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors duration-300 flex items-center gap-2 text-xs sm:text-sm"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              User Profile
                            </button>
                            <button
                              onClick={handleLogout}
                              className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors duration-300 flex items-center gap-2 text-xs sm:text-sm"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                              </svg>
                              Log out
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Log In and Sign Up Buttons - When Not Signed In */
                    <>
                      {/* Log In Button */}
                      <a 
                        href="/login" 
                        className="text-gray-300 hover:text-white transition-colors duration-300 px-1.5 sm:px-2 py-0.5 text-xs font-medium inline-block"
                      >
                        Log In
                      </a>
                      
                      {/* Sign Up Button */}
                      <a 
                        href="/sign-up" 
                        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-2 sm:px-3 py-0.5 rounded-full text-xs font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl inline-block"
                      >
                        Sign Up
                      </a>
                    </>
                  )}
                </>
              )}
              {/* Help button - always visible */}
              <button
                onClick={onHelpClick}
                className="ml-1 inline-flex items-center justify-center w-6 h-6 rounded-full border border-white/20 text-gray-300 hover:text-white hover:bg-white/10 transition"
                title="Help"
                aria-label="Show help tour"
              >
                <FaInfoCircle className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Top glow effect */}
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent"></div>
    </header>
  );
});

function CreateVideo() {
  // Define page type for namespaced session storage
  const PAGE_TYPE = PAGE_TYPES.VIDEO;
  
  const [showTour, setShowTour] = useState(false);
 
  // Get session variable userDetails (shared across all pages, not namespaced)
  const [userDetails, setUserDetails] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("userDetails")) || {};
    } catch (error) {
      console.error("Error parsing userDetails:", error);
      return {};
    }
  });

  // Utility function to safely store data in sessionStorage with quota management
  // Now uses namespaced keys based on PAGE_TYPE
  const safeSessionStorageSet = (key, data) => {
    // Use the helper function which includes namespacing and quota management
    return setSessionData(key, data, PAGE_TYPE);
  };

  // Utility function to safely get data from sessionStorage
  // Now uses namespaced keys based on PAGE_TYPE
  const safeSessionStorageGet = (key) => {
    return getSessionData(key, PAGE_TYPE);
  };

  // Utility function to compress images before storage
  const compressImageForStorage = (file, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        
        resolve({
          dataUrl: compressedDataUrl,
          name: file.name,
          type: file.type,
          size: file.size
        });
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Listen for changes to userDetails in sessionStorage
  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const newUserDetails = JSON.parse(sessionStorage.getItem("userDetails")) || {};
        // Only update if the content actually changed to prevent unnecessary re-renders
        setUserDetails(prevUserDetails => {
          if (JSON.stringify(prevUserDetails) !== JSON.stringify(newUserDetails)) {
            return newUserDetails;
          }
          return prevUserDetails;
        });
      } catch (error) {
        console.error("Error parsing userDetails:", error);
      }
    };

    // Listen for storage events
    window.addEventListener('storage', handleStorageChange);

    // Also check for changes every second (as a fallback for same-window updates)
    const intervalId = setInterval(handleStorageChange, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, []);
  
  // Add state for business details warning
  const [showBusinessWarning, setShowBusinessWarning] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState('Catalog'); // Default style
const [selectedEvent, setSelectedEvent] = useState(null); // Optional event

  // Check business details on component mount
  useEffect(() => {
    if (!userDetails?.business || 
        (!userDetails.business.name && 
        !userDetails.business.description && 
        !userDetails.business.location)) {
      setShowBusinessWarning(true);
    } else {
      setShowBusinessWarning(false);
    }
  }, [userDetails]);
  
  // ---------------------------
  // Component States
  // ---------------------------
  const { user } = useUser();
  const { getToken } = useAuth();

  // DEFAULT VALUES FOR THE FORM
  // const [selectedPlatform, setSelectedPlatform] = useState("instagram");
  // const [instagramPostType, setInstagramPostType] = useState("feed");
  // const [feedContentSource, setFeedContentSource] = useState("ai_image");  
  // const [contentMode, setContentMode] = useState("write");
  // const [mediaSource, setMediaSource] = useState("generate");

  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [instagramPostType, setInstagramPostType] = useState("reel"); // Video-only
  const [feedContentSource, setFeedContentSource] = useState("");  
  const [contentMode, setContentMode] = useState("");
  const [mediaSource, setMediaSource] = useState("");
  const [reelDuration, setReelDuration] = useState(4); // Default 5 seconds
  const [reelDurationError, setReelDurationError] = useState(""); // Add this line for reel duration error state

  // Add effect to reset aspect ratio when post type changes
  // useEffect(() => {
  //   if (instagramPostType && !isLoadingFromLandingPage) {
  //     setAspectRatio("Portrait");
  //   }
  // }, [instagramPostType, isLoadingFromLandingPage]);

  const [enableGenerateContentButton, setEnableGenerateContentButton] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [message, setMessage] = useState("");
  const [originalUploadedImages, setOriginalUploadedImages] = useState([]);
  const [referenceUploadedImages, setReferenceUploadedImages] = useState([]);
  const [aspectRatio, setAspectRatio] = useState("Portrait");
  const [generatedImgContent, setGeneratedImgContent] = useState(null);
  const [generatedVidContent, setGeneratedVidContent] = useState(null);
  const [contentGenerated, setContentGenerated] = useState(false);
  const [generateContentLabel, setGenerateContentLabel] = useState("Generate Content");
  // New state for slider controls
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishStatus, setPublishStatus] = useState({ type: null, message: "" });
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [createPoster, setCreatePoster] = useState(true);
  const [messageError, setMessageError] = useState("");
  const [newContentPlaceholder, setNewContentPlaceholder] = useState("Describe in as much details what would you like to see in the final image / video");
  const [creditInfo, setCreditInfo] = useState({ total_cost: 0, credit_left_tokens: 0, credit_left_amt: 0 });
  const [hasApiCredits, setHasApiCredits] = useState(false);
  const messageTextareaRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const textboxFileInputRef = React.useRef(null); // Add new ref for textbox file input
  const errorPopupRef = React.useRef(null); // Ref for dismissing error popup on outside click
  const [uploadError, setUploadError] = useState("");
  const [referenceUploadError, setReferenceUploadError] = useState("");
  const [ownImageUploadError, setOwnImageUploadError] = useState("");
  const [textboxUploadedImages, setTextboxUploadedImages] = useState([]); // Add new state for textbox uploaded images
  const [textboxUploadError, setTextboxUploadError] = useState(""); // Add new state for textbox upload error
  const [contentTimestamp, setContentTimestamp] = useState("");
  const [apiError, setApiError] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const generatedContentRef = React.useRef(null);
  const abortControllerRef = React.useRef(null); // Add this line for abort controller
  const navigate = useNavigate();
  // State for lightbox functionality
  const [viewerIsOpen, setViewerIsOpen] = useState(false);
  const [currentLightboxImage, setCurrentLightboxImage] = useState(0);
  // Session-wide image history and selection across generations
  const [generatedImgHistory, setGeneratedImgHistory] = useState([]);
  const [sessionSelectedImageIndex, setSessionSelectedImageIndex] = useState(null);
  const [isAspectRatioExpanded, setIsAspectRatioExpanded] = useState(false);
  const [isAiMagicLoading, setIsAiMagicLoading] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  // Add this with your other state declarations
  const [isPlaying, setIsPlaying] = useState(false);
  // Add new state for timer
  const [timeRemaining, setTimeRemaining] = useState(0);

  const [businessDetailsError, setBusinessDetailsError] = useState("");
  
  // Additional states needed for CreateVideoEditor component
  const [creationInputMode, setCreationInputMode] = useState("freeText");
  const [selectedCreationOption, setSelectedCreationOption] = useState("create_image");
  const [selectedModel, setSelectedModel] = useState("free");
  const [isModelCollapsed, setIsModelCollapsed] = useState(false);
  const [isOutputCollapsed, setIsOutputCollapsed] = useState(false);
  const [resolution, setResolution] = useState("720p"); // Default to 720p for video generation
  const [mode, setMode] = useState("pro");
  const [imageResolution, setImageResolution] = useState("FHD");
  const [outputSamples, setOutputSamples] = useState(1);
  const [isAudioCollapsed, setIsAudioCollapsed] = useState(true);
  const [audioOption, setAudioOption] = useState("");
  const [audioText, setAudioText] = useState("");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedDresses, setSelectedDresses] = useState([]);
  const [selectedHumanModel, setSelectedHumanModel] = useState({});
  const [productType, setProductType] = useState('');
  const [productItem, setProductItem] = useState('');
  const [realLooks, setRealLooks] = useState(false);
  const [selectedAIModel, setSelectedAIModel] = useState('auto');

  // Credits-based feature gating: 1080p disabled if credits <= 126
  const { userDetails: ctxUserDetails } = useUserDetails();
  const effectiveCreditAmt = Number(
    (ctxUserDetails && ctxUserDetails.credit_left_amt != null ? ctxUserDetails.credit_left_amt :
      (userDetails && userDetails.credit_left_amt != null ? userDetails.credit_left_amt : 0))
  );
  const gates = buildFeatureGates(effectiveCreditAmt);

  // Ensure resolution is set for video generation (reel post type)
  useEffect(() => {
    if (instagramPostType === 'reel' && !resolution) {
      setResolution('720p');
    }
  }, [instagramPostType, resolution]);

  // Auto-downgrade resolution if 1080p becomes disabled
  useEffect(() => {
    // Only apply auto-downgrade if resolution is set
    if (!resolution) return;
    const result = applyAutoDowngrade({ resolution, mode }, gates);
    if (result.resolution !== resolution) setResolution(result.resolution);
    if (result.mode !== mode) setMode(result.mode);
  }, [gates.disable1080p, gates.disable720p, gates.disablePremium, resolution, mode]);

  // Normalize and refresh credit info displayed in header without page reload
  const refreshHeaderCredits = useCallback((serverCredit) => {
    try {
      if (!serverCredit || typeof serverCredit !== 'object') {
        setCreditInfo({ total_cost: 0, credit_left_tokens: 0, credit_left_amt: 0 });
        return;
      }
      const rawAmt = serverCredit.credit_left_amt;
      const rawTokens = serverCredit.credit_left_tokens;
      // Prefer explicit amount if provided, fallback to tokens
      const normalizedAmt = rawAmt != null ? Number(rawAmt) : (rawTokens != null ? Number(rawTokens) : 0);
      
      // Update local creditInfo state
      setCreditInfo((prev) => ({
        ...prev,
        ...serverCredit,
        credit_left_amt: Number.isFinite(normalizedAmt) ? normalizedAmt : 0,
      }));
      
      // Also update userDetails to keep it in sync
      setUserDetails((prev) => {
        if (!prev) return prev;
        const updatedDetails = {
          ...prev,
          credit_left_amt: Number.isFinite(normalizedAmt) ? normalizedAmt : 0,
        };
        // Persist to sessionStorage so it's available across page refreshes
        try {
          sessionStorage.setItem("userDetails", JSON.stringify(updatedDetails));
        } catch (e) {
          console.error("Failed to update sessionStorage:", e);
        }
        return updatedDetails;
      });
    } catch (_) {
      // Keep previous state on failure
    }
  }, [setUserDetails]);

  const adjustTextareaHeight = (element) => {
    if (element) {
      element.style.height = 'auto';
      element.style.height = `${element.scrollHeight}px`;
    }
  };

  useEffect(() => {
    if (messageTextareaRef.current) {
      adjustTextareaHeight(messageTextareaRef.current);
    }
  }, [message]);

  // Accept pending edit image from other pages (e.g., Dashboard) and load into reference upload
  useEffect(() => {
    const applyPendingEditForVideo = async () => {
      try {
        const pendingUrl = sessionStorage.getItem('pendingEditImageUrl');
        const pendingName = sessionStorage.getItem('pendingEditImageName') || `reference-${Date.now()}.png`;
        const pendingProductData = sessionStorage.getItem('pendingVideoProductData');
        
        if (!pendingUrl) return;

        // Set loading state to true when starting image upload
        setIsImageUploading(true);

        // Clear immediately to avoid re-applying on future visits
        try {
          sessionStorage.removeItem('pendingEditImageUrl');
          sessionStorage.removeItem('pendingEditImageName');
          sessionStorage.removeItem('pendingVideoProductData');
        } catch (_) {}

        // Apply product data if available
        if (pendingProductData) {
          try {
            const productData = JSON.parse(pendingProductData);
            if (productData.productType) setProductType(productData.productType);
            if (productData.productItem) setProductItem(productData.productItem);
            if (productData.selectedDresses) setSelectedDresses(productData.selectedDresses);
            if (productData.selectedHumanModel) setSelectedHumanModel(productData.selectedHumanModel);
            if (productData.selectedEvent) setSelectedEvent(productData.selectedEvent);
            if (productData.selectedStyle) setSelectedStyle(productData.selectedStyle);
            if (productData.realLooks !== undefined) setRealLooks(productData.realLooks);
          } catch (err) {
            console.error('Error parsing product data:', err);
          }
        }

        // Fetch via backend proxy to avoid CORS and preserve auth
        const token = await getToken();
        const proxyUrl = `${process.env.REACT_APP_API_URL}/download_media?url=${encodeURIComponent(pendingUrl)}&filename=${encodeURIComponent(pendingName)}`;
        let response;
        try {
          response = await fetch(proxyUrl, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` },
          });
        } catch (err) {
          console.error('Network error while fetching pending edit image:', err);
          toast.error('Could not load image. Please upload manually.');
          return;
        }
        if (!response.ok) {
          toast.error('Failed to fetch the image for video creation.');
          return;
        }

        const contentType = response.headers.get('Content-Type') || '';
        const blob = await response.blob();
        const blobType = contentType || blob.type || 'image/png';
        const ext = blobType.includes('jpeg') || blobType.includes('jpg') ? 'jpg' : (blobType.includes('png') ? 'png' : 'png');
        const fileName = pendingName.includes('.') ? pendingName : `${pendingName}.${ext}`;
        const file = new File([blob], fileName, { type: blobType });

        const compressedData = await compressImageForStorage(file);
        
        // Apply product data to the image object if available
        let productImageData = {};
        if (pendingProductData) {
          try {
            const productData = JSON.parse(pendingProductData);
            if (productData.textboxImages && productData.textboxImages.length > 0) {
              const firstImageData = productData.textboxImages[0];
              productImageData = {
                productType: firstImageData.productType,
                productItem: firstImageData.productItem,
                productView: firstImageData.productView,
                productCoverage: firstImageData.productCoverage,
                productCloseupDetail: firstImageData.productCloseupDetail,
                ProductImgDtl: firstImageData.ProductImgDtl
              };
            }
          } catch (err) {
            console.error('Error applying product data to image:', err);
          }
        }
        
        const newImageObj = {
          file,
          preview: URL.createObjectURL(file),
          dataUrl: compressedData?.dataUrl || '',
          name: file.name,
          type: file.type,
          size: file.size,
          ...productImageData
        };

        // Replace existing reference images with the new one
        setTextboxUploadedImages([newImageObj]);
        toast.success('Loaded image into video creator.');
      } catch (e) {
        console.error('Error applying pending edit image for video:', e);
      } finally {
        // Set loading state to false when image upload completes (success or error)
        setIsImageUploading(false);
      }
    };
    applyPendingEditForVideo();
  }, []);

  // Sync the local editable content with appropriate generated content whenever new content is generated.
  useEffect(() => {
    const currentContent = instagramPostType === "reel" ? generatedVidContent : generatedImgContent;
    if (currentContent) {
      setEditedContent(currentContent.content);
    }
  }, [generatedImgContent, generatedVidContent, instagramPostType]);

  // Toggle edit mode: if saving, update the generated content state and save to Firestore
  const handleToggleEdit = async () => {
    try {
      // Build the list of all generated images across history or fallback to current batch
      const flattenedImages = Array.isArray(generatedImgHistory)
        ? generatedImgHistory.flatMap((entry) => (Array.isArray(entry.images) ? entry.images : []))
        : [];
      const singleBatch = (flattenedImages.length === 0 && Array.isArray(generatedImgContent?.images))
        ? generatedImgContent.images
        : [];
      const allImages = flattenedImages.length > 0 ? flattenedImages : singleBatch;

      if (!Array.isArray(allImages) || allImages.length === 0) {
        toast.error('No generated image available to use as reference.');
        return;
      }

      const totalImages = allImages.length;
      const effectiveIndex = (typeof sessionSelectedImageIndex === 'number' && sessionSelectedImageIndex >= 0 && sessionSelectedImageIndex < totalImages)
        ? sessionSelectedImageIndex
        : (totalImages - 1);
      const selectedItem = allImages[effectiveIndex];

      if (!selectedItem || !selectedItem.url) {
        toast.error('Could not determine the selected image.');
        return;
      }

      if (selectedItem.isVideo) {
        toast.error('Selected content is a video. Please select an image to use as a reference.');
        return;
      }

      // Fetch the image data and construct a File object compatible with reference uploader
      let response;
      try {
        response = await fetch(selectedItem.url, { mode: 'cors' });
      } catch (err) {
        console.error('CORS error while fetching image:', err);
        toast.error('Could not load image due to CORS. Please download and upload manually.');
        return;
      }

      if (!response.ok) {
        toast.error('Failed to fetch the selected image.');
        return;
      }

      const blob = await response.blob();
      const blobType = blob.type || 'image/png';
      const ext = blobType.includes('jpeg') || blobType.includes('jpg') ? 'jpg' : (blobType.includes('png') ? 'png' : 'png');
      const fileName = `reference-${Date.now()}.${ext}`;
      const file = new File([blob], fileName, { type: blobType });

      // Compress and prepare preview/dataUrl similar to manual upload flow
      const compressedData = await compressImageForStorage(file);
      const newImageObj = {
        file,
        preview: URL.createObjectURL(file),
        dataUrl: compressedData?.dataUrl || '',
        name: file.name,
        type: file.type,
        size: file.size,
      };

      // Revoke previews for any existing reference images to avoid leaks
      try {
        if (Array.isArray(textboxUploadedImages)) {
          textboxUploadedImages.forEach(img => {
            if (img && img.preview) {
              try { URL.revokeObjectURL(img.preview); } catch (_) {}
            }
          });
        }
      } catch (_) {}

      // Replace existing reference images with the newly selected one
      setTextboxUploadedImages([newImageObj]);

      toast.success('Set current image as reference.');
    } catch (error) {
      console.error('Error setting reference image from generated output:', error);
      toast.error('Could not set reference image.');
    }
  };

  // ---------------------------
  // Helper Functions
  // ---------------------------
  // Call backend AI Magic endpoint with the message as input and update the message text
  const handleAiMagic = async () => {
    setIsAiMagicLoading(true); // show wait overlay when request starts
    try {
      setMessage(""); // Clear existing message to prepare for streaming
      
      // Clear the placeholder and any error message when AI Magic is clicked
      setNewContentPlaceholder("");

      //Clear the error message when AI Magic is clicked
      setMessageError("");
      
      const token = await getToken();
      // // console.log("token:", token);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/ai_magic`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ 
            email: user?.primaryEmailAddress?.emailAddress,
            platform: selectedPlatform,
            instagramPostType: instagramPostType
          }),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to apply AI magic");
      }
      
      const contentType = response.headers.get("content-type");
      
      // Handle streaming response if content type is text/plain
      if (contentType && contentType.includes("text/plain")) {
        // First, collect the entire response
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let completeMessage = "";
        
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }
          
          // Decode the stream chunk and add to complete message
          const chunk = decoder.decode(value, { stream: true });
          completeMessage += chunk;
        }
        
        // Now display the message character by character with a delay
        let displayedMessage = "";
        for (let i = 0; i < completeMessage.length; i++) {
          await new Promise(resolve => setTimeout(resolve, 25)); // 25ms delay between characters
          displayedMessage += completeMessage[i];
          setMessage(displayedMessage);
        }
      } else {
        // Fallback for non-streaming response
        const data = await response.json();
        
        // Apply the same typing effect to non-streaming responses
        const completeMessage = data.modifiedMessage || "Create engaging content for your business";
        let displayedMessage = "";
        
        for (let i = 0; i < completeMessage.length; i++) {
          await new Promise(resolve => setTimeout(resolve, 25)); // 25ms delay between characters
          displayedMessage += completeMessage[i];
          setMessage(displayedMessage);
        }
      }
      
      toast.success("AI magic applied!");
    } catch (error) {
      console.error("Error in AI Magic:", error);
      setMessageError(error.message || "Failed to apply AI magic");
      toast.error(error.message || "Failed to apply AI magic");
    } finally {
      setIsAiMagicLoading(false); // hide wait overlay when done
    }
  };
  // Update state with uploaded files and create preview URLs for image thumbnails
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // For contentMode="upload" (reference image upload)
    // // console.log("contentMode:", contentMode);
    if (1 <= 2) {
      // Allow up to 12 images for reference upload
      if (files.length > 12) {
        setReferenceUploadError("Maximum 12 images can be uploaded. Please select 12 or less images.");
        return;
      }
      
      // Check if adding these files would exceed the 12 image limit
      if (referenceUploadedImages.length + files.length > 12) {
        setReferenceUploadError(`Maximum 12 images can be uploaded. You can only add ${12 - referenceUploadedImages.length} more image(s).`);
        return;
      }
      
      // Validate file format
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const invalidFiles = files.filter(file => !validImageTypes.includes(file.type));
      
      if (invalidFiles.length > 0) {
        setReferenceUploadError("Please upload only JPEG, JPG, or PNG image files.");
        return;
      }
      
      // Clear any previous error when upload is valid
      setReferenceUploadError("");
      
      const filePreviews = files.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));
      
      // Add new images to existing ones instead of replacing
      setReferenceUploadedImages(prevImages => [...prevImages, ...filePreviews]);
      
      // Clear the file input value to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }
    
    // Get the current images and setter based on mediaSource
    const currentImages = mediaSource === "original" ? originalUploadedImages : referenceUploadedImages;
    const setCurrentImages = mediaSource === "original" ? setOriginalUploadedImages : setReferenceUploadedImages;
    
    // Add validation for reference mode - only 1 image allowed
    if (mediaSource === "reference") {
      if (files.length > 1) {
        setUploadError("Only 1 reference image can be uploaded. Please select just one image.");
        return;
      }
      
      // Validate file format
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const invalidFiles = files.filter(file => !validImageTypes.includes(file.type));
      
      if (invalidFiles.length > 0) {
        setUploadError("Please upload only JPEG, JPG, or PNG image files.");
        return;
      }
      
      // Clear any previous error when upload is valid
      setUploadError("");
      
      // Clear existing images before adding new one
      setCurrentImages([]);
      
      const filePreviews = files.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));
      
      setCurrentImages(filePreviews);
      return;
    }
    
    // Add validation for original mode - only 12 images allowed
    if (mediaSource === "original") {
      if (files.length > 12) {
        setUploadError("Maximum 12 images can be uploaded per post. Please select 12 or less images.");
        return;
      }
      
      if (currentImages.length + files.length > 12) {
        setUploadError("Maximum 12 images can be uploaded per post. You can only add " + (12 - currentImages.length) + " more image(s).");
        return;
      }
    }
    
    // Clear any previous error when upload is valid
    setUploadError("");
    
    const filePreviews = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setCurrentImages((prevImages) => [...prevImages, ...filePreviews]);
  };

  // Update removeImage to use the right state based on mediaSource
  const removeImage = (index) => {
    if (mediaSource === "original") {
      setOriginalUploadedImages((prevImages) => {
        const newImages = [...prevImages];
        URL.revokeObjectURL(newImages[index].preview);
        newImages.splice(index, 1);
        return newImages;
      });
    } else {
      setReferenceUploadedImages((prevImages) => {
        const newImages = [...prevImages];
        URL.revokeObjectURL(newImages[index].preview);
        newImages.splice(index, 1);
        return newImages;
      });
    }
  };

  // Add new function to handle textbox file uploads
  const handleTextboxFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Allow up to 12 images for textbox upload
    if (files.length > 12) {
      setTextboxUploadError("Maximum 12 images can be uploaded. Please select 12 or less images.");
      return;
    }
    
    // Check if adding these files would exceed the 12 image limit
    if (textboxUploadedImages.length + files.length > 12) {
      setTextboxUploadError(`Maximum 12 images can be uploaded. You can only add ${12 - textboxUploadedImages.length} more image(s).`);
      return;
    }
    
    // Validate file format
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const invalidFiles = files.filter(file => !validImageTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      setTextboxUploadError("Please upload only JPEG, JPG, or PNG image files.");
      return;
    }
    
    // Clear any previous error when upload is valid
    setTextboxUploadError("");
    
    // Convert files to compressed base64 for sessionStorage compatibility
    const processFiles = async () => {
      const filePreviews = await Promise.all(files.map(async (file) => {
        try {
          // Compress image for storage
          const compressedData = await compressImageForStorage(file);
          return {
            file, // Keep the original file object for FormData
            preview: URL.createObjectURL(file),
            dataUrl: compressedData.dataUrl, // Compressed data for session storage
            name: file.name,
            type: file.type,
            size: file.size
          };
        } catch (error) {
          console.error('Error compressing image:', error);
          // Fallback to original method if compression fails
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                file, // Keep the original file object for FormData
                preview: URL.createObjectURL(file),
                dataUrl: e.target.result,
                name: file.name,
                type: file.type,
                size: file.size
              });
            };
            reader.readAsDataURL(file);
          });
        }
      }));
      
      // Add new images to existing ones
      setTextboxUploadedImages(prevImages => [...prevImages, ...filePreviews]);
    };
    
    processFiles();
    
    // Clear the file input value to allow selecting the same file again
    if (textboxFileInputRef.current) {
      textboxFileInputRef.current.value = "";
    }
  };

  // Add new function to remove images from textbox
  const removeTextboxImage = (index) => {
    setTextboxUploadedImages((prevImages) => {
      if (!Array.isArray(prevImages) || index < 0 || index >= prevImages.length) return prevImages;
      const newImages = [...prevImages];
      const previewUrl = newImages[index]?.preview;
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  // Add new state for storing session key
  // const [sessionContentKey, setSessionContentKey] = useState("generatedContentData");

  // Add flag to track when loading from landing page
  const [isLoadingFromLandingPage, setIsLoadingFromLandingPage] = useState(false);

  // Load saved content from sessionStorage when component mounts
  useEffect(() => {
    try {
      // First check for content from landing page (use namespaced key)
      const landingPageContent = getSessionData('landingPageContent', PAGE_TYPE);
      if (landingPageContent) {
        const parsedLandingContent = landingPageContent;
        
        // Console log the saved session data
        // console.log('Landing page content loaded:', parsedLandingContent);
        
        // Set flag to prevent overriding values
        setIsLoadingFromLandingPage(true);
        
        // Load content from landing page only if data exists
        if (parsedLandingContent.message && parsedLandingContent.message.trim()) {
          // console.log('Setting message:', parsedLandingContent.message);
          setMessage(parsedLandingContent.message);
        // } else {
        //   console.log('No message found in landing page data');
        }
        
        /* Video-only: ignore instagramPostType from landing page
        if (parsedLandingContent.instagramPostType) {
          // console.log('Setting instagramPostType:', parsedLandingContent.instagramPostType);
          setInstagramPostType(parsedLandingContent.instagramPostType);
        // } else {
        //   console.log('No instagramPostType found in landing page data');
        }
        */
        console.log("1 selectedStyle", parsedLandingContent.selectedStyle);
        if (parsedLandingContent.selectedStyle) {
          // console.log('Setting selectedStyle:', parsedLandingContent.selectedStyle);
          setSelectedStyle(parsedLandingContent.selectedStyle);
        // } else {
        //   console.log('No selectedStyle found in landing page data');
        }
        
        if (parsedLandingContent.aspectRatio) {
          // console.log('Setting aspectRatio:', parsedLandingContent.aspectRatio);
          setAspectRatio(parsedLandingContent.aspectRatio);
        // } else {
        //   console.log('No aspectRatio found in landing page data');
        }
        
        // Reset flag after a short delay to allow state updates to complete
        setTimeout(() => {
          setIsLoadingFromLandingPage(false);
        }, 100);
        
        // Set default values for other required fields
        setSelectedPlatform("instagram");
        setFeedContentSource("ai_image");
        setContentMode("write");
        setMediaSource("generate");
        setInstagramPostType("reel"); // Force video
        
        // Load textbox images if they exist
        if (parsedLandingContent.textboxImages && parsedLandingContent.textboxImages.length > 0) {
          // console.log('Loading textbox images:', parsedLandingContent.textboxImages);
          
          // Convert dataUrls back to file objects
          const convertedImages = parsedLandingContent.textboxImages.map((imgData) => {
            // Convert dataUrl to blob
            const byteString = atob(imgData.dataUrl.split(',')[1]);
            const mimeString = imgData.dataUrl.split(',')[0].split(':')[1].split(';')[0];
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);
            
            for (let i = 0; i < byteString.length; i++) {
              ia[i] = byteString.charCodeAt(i);
            }
            
            const blob = new Blob([ab], { type: mimeString });
            const file = new File([blob], imgData.name, { type: imgData.type });
            
            return {
              file,
              preview: URL.createObjectURL(blob),
              dataUrl: imgData.dataUrl,
              name: imgData.name,
              type: imgData.type,
              size: imgData.size,
              ProductImgDtl: imgData.ProductImgDtl
            };
          });
          
          setTextboxUploadedImages(convertedImages);
        } 
        // else {
        //   console.log('No textbox images found in landing page data');
        // }
        
        // Clear the landing page content after loading (use namespaced key)
        // removeSessionData('landingPageContent', PAGE_TYPE);
        
        // Don't load from regular session storage if we loaded from landing page
        return;
      }
      
      // If no landing page content, load from regular session storage
      const savedContent = safeSessionStorageGet('generatedContentData');
      if (savedContent) {
        const parsedContent = savedContent;
        
        // Restore all relevant states from saved content
        if (parsedContent.generatedImgContent) {
          setGeneratedImgContent(parsedContent.generatedImgContent);
        }
        if (parsedContent.generatedVidContent) {
          setGeneratedVidContent(parsedContent.generatedVidContent);
        }
        // Restore selected dresses if present
        if (Array.isArray(parsedContent.selectedDresses)) {
          setSelectedDresses(parsedContent.selectedDresses);
        }
        
        // Restore session-wide generated image history (if available)
        if (Array.isArray(parsedContent.generatedImgHistory)) {
          setGeneratedImgHistory(parsedContent.generatedImgHistory);
        } else if (parsedContent.generatedImgContent) {
          // Backwards-compat: seed history from last single content if present
          const seedEntry = {
            timestamp: parsedContent.contentTimestamp || Date.now().toString(),
            content: parsedContent.generatedImgContent.content || "",
            images: parsedContent.generatedImgContent.images || []
          };
          setGeneratedImgHistory([seedEntry]);
        }
        
        // Check if any content exists to set contentGenerated flag
        if (parsedContent.generatedImgContent || parsedContent.generatedVidContent) {
          setContentGenerated(true);
          setGenerateContentLabel("Regenerate");
        }
        
        setSelectedImages(parsedContent.selectedImages || [0]);
        setSelectedImageIndex(parsedContent.selectedImageIndex || 0);
        setContentTimestamp(parsedContent.contentTimestamp || "");
        
        // Set edited content based on the active content type
        const activeContent = parsedContent.generatedVidContent || parsedContent.generatedImgContent;
        setEditedContent(parsedContent.editedContent || activeContent?.content || "");
        
        // Restore session-wide selected image index, default to latest image
        if (typeof parsedContent.sessionSelectedImageIndex === 'number') {
          setSessionSelectedImageIndex(parsedContent.sessionSelectedImageIndex);
        } else {
          // compute latest index from history
          const history = Array.isArray(parsedContent.generatedImgHistory)
            ? parsedContent.generatedImgHistory
            : (parsedContent.generatedImgContent ? [{ images: parsedContent.generatedImgContent.images || [] }] : []);
          const totalImages = history.reduce((sum, entry) => sum + (Array.isArray(entry.images) ? entry.images.length : 0), 0);
          setSessionSelectedImageIndex(totalImages > 0 ? totalImages - 1 : null);
        }
        
        // Load textbox images if they exist in regular session storage
        if (parsedContent.textboxImages && parsedContent.textboxImages.length > 0) {
          
          // Convert dataUrls back to file objects
          const convertedImages = parsedContent.textboxImages.map((imgData) => {
            // Convert dataUrl to blob
            const byteString = atob(imgData.dataUrl.split(',')[1]);
            const mimeString = imgData.dataUrl.split(',')[0].split(':')[1].split(';')[0];
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);
            
            for (let i = 0; i < byteString.length; i++) {
              ia[i] = byteString.charCodeAt(i);
            }
            
            const blob = new Blob([ab], { type: mimeString });
            const file = new File([blob], imgData.name, { type: imgData.type });
            
            return {
              file,
              preview: URL.createObjectURL(blob),
              dataUrl: imgData.dataUrl,
              name: imgData.name,
              type: imgData.type,
              size: imgData.size,
              ProductImgDtl: imgData.ProductImgDtl
            };
          });
          
          setTextboxUploadedImages(convertedImages);
        }
        
        // Restore form selections if they exist
        if (parsedContent.selectedPlatform) setSelectedPlatform(parsedContent.selectedPlatform);
        if (parsedContent.instagramPostType) setInstagramPostType(parsedContent.instagramPostType);
        if (parsedContent.feedContentSource) setFeedContentSource(parsedContent.feedContentSource);
        if (parsedContent.contentMode) setContentMode(parsedContent.contentMode);
        if (parsedContent.mediaSource) setMediaSource(parsedContent.mediaSource);
        if (parsedContent.selectedProduct) setSelectedProduct(parsedContent.selectedProduct);
        if (parsedContent.selectedEvent) setSelectedEvent(parsedContent.selectedEvent);
        if (parsedContent.message) setMessage(parsedContent.message);
        if (parsedContent.aspectRatio) setAspectRatio(parsedContent.aspectRatio);
        if (parsedContent.selectedDresses) setSelectedDresses(parsedContent.selectedDresses);
        if (parsedContent.selectedHumanModel) setSelectedHumanModel(parsedContent.selectedHumanModel);
        if (parsedContent.productType) setProductType(parsedContent.productType);
        if (parsedContent.productItem) setProductItem(parsedContent.productItem);
        if (typeof parsedContent.realLooks === 'boolean') setRealLooks(parsedContent.realLooks);
      }
    } catch (error) {
      console.error("Error loading saved content from session storage:", error);
    }
  }, [PAGE_TYPE]);

  // useEffect(() => {
  //   console.log('State values changed:', {
  //     message: message,
  //     instagramPostType: instagramPostType,
  //     selectedStyle: selectedStyle,
  //     aspectRatio: aspectRatio
  //   });
  // }, [message, instagramPostType, selectedStyle, aspectRatio]);

  // Add this ref to track ongoing requests
  const isGeneratingContentRef = React.useRef(false);

  // Calculate total time based on post type
  const totalTime = instagramPostType === "reel" ? 120 : 20; // 2:00 for reels

  // Update timeRemaining when post type changes
  const timeNeeded = 20 // 0:20 for feed and story
  useEffect(() => {
    if (instagramPostType === "reel") {
      setTimeRemaining(120); // 2:00 for reels
    } else {
      setTimeRemaining(timeNeeded);
    }
  }, [instagramPostType]);

  // Countdown timeRemaining
  useEffect(() => {
    if (isGeneratingContent && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => Math.max(0, prev - 1));
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isGeneratingContent, timeRemaining]);

  // Enhanced fetch with retry mechanism for mobile browsers
  const fetchWithRetry = async (url, options, maxRetries = 3) => {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, options);
        return response;
      } catch (error) {
        lastError = error;
        console.warn(`Fetch attempt ${attempt} failed:`, error);
        
        // Don't retry on abort errors (user cancellation)
        if (error.name === 'AbortError') {
          throw error;
        }
        
        // Don't retry on the last attempt
        if (attempt === maxRetries) {
          break;
        }
        
        // Exponential backoff: wait 2^attempt seconds
        const delay = Math.pow(2, attempt) * 1000;
        console.log(`Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError;
  };

  const handleGenerateContent = async () => {
    // Check the ref instead of the state to prevent concurrent executions
    if (isGeneratingContentRef.current) {
      console.warn("Content generation already in progress. Skipping duplicate request.");
      return; // Exit early if already generating
    }

    // Set both the ref and the state
    isGeneratingContentRef.current = true;
    setIsGeneratingContent(true);
    setGenerationStartTime(Date.now());
    
    // Video-only: block non-reel post types
    if (instagramPostType !== "reel") {
      toast.error("Only video (Reel) generation is supported on this page.");
      setIsGeneratingContent(false);
      isGeneratingContentRef.current = false;
      return;
    }
    
    // Scroll to content skeleton - use setTimeout to ensure component is rendered
    setTimeout(() => {
      // Scroll to the generated content section instead
      if (generatedContentRef.current) {
        generatedContentRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 30);
    
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    
    // Reset error states
    setMessageError("");
    setReferenceUploadError("");
    setOwnImageUploadError("");
    setApiError("");

    // Validate content type selection
    if (!instagramPostType || !["feed", "reel", "story"].includes(instagramPostType)) {
      toast.error("Please select Image (Feed/Story) or Video (Reel)");
      setIsGeneratingContent(false);
      isGeneratingContentRef.current = false;
      return;
    }

    // Validate reel duration for reels
    // const reelDuration = 10; // removed local shadow; using state reelDuration instead
    
    /* Non-video: image upload requirement disabled
    // If no images have been uploaded anywhere, show a popup and stop
    const noImagesUploaded =
      (!originalUploadedImages || originalUploadedImages.length === 0) &&
      (!referenceUploadedImages || referenceUploadedImages.length === 0) &&
      (!textboxUploadedImages || textboxUploadedImages.length === 0);

    if (noImagesUploaded) {
      const uploadMsg = "Please upload at least one image to continue.";
      setTextboxUploadError(uploadMsg);
      toast.error(uploadMsg);
      if (textboxFileInputRef.current) {
        textboxFileInputRef.current.focus();
      } else if (fileInputRef.current) {
        fileInputRef.current.focus();
      }
      setIsGeneratingContent(false);
      isGeneratingContentRef.current = false;
      return;
    }
    */

    /* Non-video: image upload validations disabled
    // Validate image upload when "original" or "reference" media source is selected
    if (feedContentSource === "own_image" && (!originalUploadedImages || originalUploadedImages.length === 0)) {
      setOwnImageUploadError("Please upload at least one image to continue.");
      if (fileInputRef.current) {
        fileInputRef.current.focus();
      }
      // Reset the generation state before returning
      setIsGeneratingContent(false);
      isGeneratingContentRef.current = false;
      return; // Stop execution if validation fails
    }

    // Validate reference images for contentMode="upload"
    if (contentMode === "upload" && (!referenceUploadedImages || referenceUploadedImages.length === 0)) {
      setReferenceUploadError("Please upload at least one image to continue.");
      // Focus on the file input
      if (fileInputRef.current) {
        fileInputRef.current.focus();
      }
      // Reset the generation state before returning
      // return; // Stop execution if validation fails
      setIsGeneratingContent(false);
      isGeneratingContentRef.current = false;
      return;
    }
    */
    
    /* Non-video: image file type validations disabled
    // Validate image formats for original uploads
    if (feedContentSource === "own_image" && originalUploadedImages.length > 0) {
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const invalidImages = originalUploadedImages.filter(img => !validImageTypes.includes(img.file.type));
      
      if (invalidImages.length > 0) {
        setUploadError("Please upload only JPEG, JPG, or PNG image files.");
        // Focus on the file input
        if (fileInputRef.current) {
          fileInputRef.current.focus();
        }
        // Reset the generation state before returning
        // return; // Stop execution if validation fails
        setIsGeneratingContent(false);
        isGeneratingContentRef.current = false;
        return;
      }
    }
    
    // Validate image formats for reference uploads
    console.log("referenceUploadedImages.length", referenceUploadedImages.length);
    if (referenceUploadedImages.length > 0) {
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const invalidImages = referenceUploadedImages.filter(img => !validImageTypes.includes(img.file.type));
      
      if (invalidImages.length > 0) {
        setUploadError("Please upload only JPEG, JPG, or PNG image files.");
        // Focus on the file input
        if (fileInputRef.current) {
          fileInputRef.current.focus();
        }
        // Reset the generation state before returning
        // 
        setIsGeneratingContent(false);
        isGeneratingContentRef.current = false;
        return;
      }
    }
    */
    
    // Clear existing content if regenerating
    if (generateContentLabel === "Regenerate") {
      if (instagramPostType === "reel") {
        setGeneratedVidContent(null);
      } else {
        setGeneratedImgContent(null);
      }
      setContentGenerated(false);
      setSelectedImages([]); // Reset selected images
    }
    
    // Resolve latest message directly from the textarea ref to avoid stale state
    const currentMessage = messageTextareaRef.current && typeof messageTextareaRef.current.value === 'string'
      ? messageTextareaRef.current.value
      : message;
    const effectiveMessage = (currentMessage || '').trim();

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("email", user?.primaryEmailAddress?.emailAddress);
      formDataToSend.append("platform", selectedPlatform);

      formDataToSend.append("instagramPostType", instagramPostType);
      formDataToSend.append("contentMode", "write");
      formDataToSend.append("feedContentSource", "ai_image");
      formDataToSend.append("creation_option", selectedCreationOption);
      formDataToSend.append("message", effectiveMessage);
      formDataToSend.append("model", selectedModel);
      formDataToSend.append("ai_model", selectedAIModel);
      /* Video-only: image resolution not needed
      formDataToSend.append("image_resolution", imageResolution);
      */
      formDataToSend.append("video_resolution", resolution);
      formDataToSend.append("video_mode", mode);
      formDataToSend.append("num_outputs", outputSamples);
      if (audioOption) {
        formDataToSend.append("audio_option", audioOption);
      }
      if (audioText) {
        formDataToSend.append("audio_text", audioText);
      }
      try {
        formDataToSend.append("userDetails", JSON.stringify(userDetails || {}));
      } catch (e) {}
      
      // Add textbox uploaded images to form data (these are the reference images)
      if (textboxUploadedImages.length > 0) {
        // Append all textbox images with unique keys as reference_img_* (backend expects this format)
        textboxUploadedImages.forEach((img, index) => {
          formDataToSend.append(`reference_img_${index}`, img.file);
          if (img.maskBlob) {
            // Optional: send mask if available; backend may ignore if unsupported
            formDataToSend.append(`reference_mask_${index}`, img.maskBlob, `mask_${index}.png`);
            if (img.maskOriginalSize) {
              try {
                formDataToSend.append(`reference_mask_meta_${index}`, JSON.stringify(img.maskOriginalSize));
              } catch (_e) {}
            }
          }
        });
        // Also append the count of reference images
        formDataToSend.append("reference_img_count", textboxUploadedImages.length.toString());
      }

      /* Video-only: no feed/story support here
      if (instagramPostType === "feed" || instagramPostType === "story") {
        formDataToSend.append("aspectRatio", aspectRatio);
      }
      */
      
      if (instagramPostType === "reel"){
        formDataToSend.append("aspectRatio", aspectRatio);
        formDataToSend.append("reelDuration", reelDuration);
      }

      // Add content style to form data
      if (selectedEvent) {
        formDataToSend.append("event", selectedEvent);
      }
      if (selectedStyle) {
        formDataToSend.append("contentStyle", selectedStyle);
      }
      // Product/image metadata for video (now using ReferenceImageModal)
      if (productType) {
        formDataToSend.append("product_type", productType);
      }
      if (productItem) {
        formDataToSend.append("product_item", productItem);
      }
      const productImgDtlToSend = textboxUploadedImages[0]?.ProductImgDtl;
      // Debug: Log ProductImgDtl being sent to API
      console.log('Sending ProductImgDtl to API:', productImgDtlToSend);
      if (productImgDtlToSend) {
        formDataToSend.append("ProductImgDtl", JSON.stringify(productImgDtlToSend));
      }
      if (Array.isArray(selectedDresses) && selectedDresses.length > 0) {
        formDataToSend.append("dresses", JSON.stringify(selectedDresses));
      }
      if (selectedHumanModel && Object.keys(selectedHumanModel).length > 0) {
        formDataToSend.append("human_model", JSON.stringify(selectedHumanModel));
      }
      // Real looks flag
      formDataToSend.append("real_looks", String(!!realLooks));

      // Show all entries passed via formdata
      // for (const [key, value] of formDataToSend.entries()) {
        // console.log(`${key}: ${value}`);
      // }

      const token = await getToken();
      
      // Use enhanced fetch with retry mechanism
      // Pass 1 as maxRetries to prevent multiple charges/generations on network glitches
      const response = await fetchWithRetry(`${process.env.REACT_APP_API_URL}/generate_content_stream`, {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend,
        signal: abortControllerRef.current.signal // Add signal to fetch request
      }, 1);

      const data = await response.json();

      // Check if the response contains an error
      if (!response.ok || data.error) {
        throw new Error(data.error || `API returned status code ${response.status}`);
      }
      
      // Parse the new JSON shape from backend: { type, timestamp, images, content, credit? }
      const imageUrls = (data && data.images) ? data.images : {};
      const contentString = (data && typeof data.content === 'string') ? data.content : '';
      const contentTimestampFromResponse = (data && data.timestamp) ? data.timestamp : '';
      const credit = data && { ...data.credit, time_taken_sec: data.time_taken_sec };
      // console.log("Credit:", credit);
      
      // Safely set credit info with fallback to default values if credit is null or invalid
      if (credit && typeof credit === 'object') {
        refreshHeaderCredits(credit);
        setHasApiCredits(true);
      } else {
        refreshHeaderCredits({ total_cost: 0, credit_left_tokens: 0 });
      }

      const imageUrlArray = Object.entries(imageUrls).map(([prompt, urls]) => {
        // Handle both single URL and array of URLs
        const urlArray = Array.isArray(urls) ? urls : [urls];
        return urlArray.map((url, index) => ({
          prompt: `${prompt}_${index}`,
          url,
        }));
      }).flat(); // Flatten the array of arrays into a single array

      let formattedContent;
      try {
        const contentJSON = JSON.parse(contentString);
        let hashtagsPart = "";
        if (Array.isArray(contentJSON.hashtags)) {
          hashtagsPart = contentJSON.hashtags.join(" ");
        } else if (typeof contentJSON.hashtags === "string") {
          hashtagsPart = contentJSON.hashtags.split(/,\s*/).join(" ");
        }
        formattedContent = `${contentJSON.caption}\n\n${contentJSON.call_to_action}\n\n${hashtagsPart}`;
      } catch (jsonError) {
        const regex = /^"([^"]+)"\s+hashtags=\[(.*?)\]\s+call_to_action='(.*?)'$/;
        const match = contentString.match(regex);
        if (match) {
          const caption = match[1].trim();
          const hashtagsStr = match[2].trim();
          const callToAction = match[3].trim();
          const hashtagArray = hashtagsStr.split(",").map((tag) => tag.trim().replace(/^'|'$/g, ""));
          formattedContent = `${caption}\n\n${callToAction}\n\n${hashtagArray.join(" ")}`;
        } else {
          // console.error("Content string format does not match expected pattern.");
          formattedContent = contentString;
        }
      }

      // Create the content object
      const contentObject = {
        images: imageUrlArray.map((item, index) => {
          const isVideo = item.url.toLowerCase().endsWith('.mp4');
          const isImage = item.url.toLowerCase().endsWith('.png') || 
                         item.url.toLowerCase().endsWith('.jpeg') || 
                         item.url.toLowerCase().endsWith('.jpg');
          
          let contentType = '';
          if (isVideo) {
            contentType = 'vid';
          } else if (isImage) {
            contentType = 'img';
          }
          
          return {
            ...item,
            isVideo: isVideo,
            contentType: contentType,
            index: index
          };
        }),
        content: formattedContent,
      };
            
      // Save to appropriate state based on post type
      if (instagramPostType === "reel") {
        setGeneratedVidContent(contentObject);
      } else {
        setGeneratedImgContent(contentObject);
      }
      setContentGenerated(true);
      setGenerateContentLabel("Regenerate");
      // Select the first image by default
      setSelectedImages([0]);
      setSelectedImageIndex(0);
      // Save the contentTimestamp from the API response in component state
      setContentTimestamp(contentTimestampFromResponse);

      // Save to sessionStorage
      const sessionData = {
        selectedImages: [0],
        selectedImageIndex: 0,
        contentTimestamp: contentTimestampFromResponse,
        editedContent: formattedContent,
        // Form selections
        selectedPlatform: selectedPlatform,
        instagramPostType: instagramPostType,
        feedContentSource: feedContentSource,
        contentMode: contentMode,
        mediaSource: mediaSource,
        selectedProduct: selectedProduct,
        selectedEvent: selectedEvent,
        message: effectiveMessage,
        aspectRatio: aspectRatio,
        textboxImages: textboxUploadedImages.map(img => ({
          dataUrl: img.dataUrl,
          name: img.name,
          type: img.type,
          size: img.size,
          ProductImgDtl: img.ProductImgDtl
        })),
        selectedDresses: selectedDresses,
        selectedHumanModel: selectedHumanModel,
        productType: productType,
        productItem: productItem,
        realLooks: realLooks
      };
      
      // Add content to appropriate key based on post type
      if (instagramPostType === "reel") {
        sessionData.generatedVidContent = contentObject;
      } else {
        sessionData.generatedImgContent = contentObject;
      }
      
      // Update session-wide image history for videos/images
      try {
        const prevSaved = safeSessionStorageGet('generatedContentData') || {};
        const prevHistory = Array.isArray(prevSaved.generatedImgHistory) ? prevSaved.generatedImgHistory : generatedImgHistory;
        const updatedHistory = Array.isArray(prevHistory) ? [...prevHistory] : [];
        updatedHistory.push({
          timestamp: contentTimestampFromResponse,
          content: formattedContent,
          images: Array.isArray(contentObject.images) ? contentObject.images : []
        });
        sessionData.generatedImgHistory = updatedHistory;

        // Compute new selected index as the last image/video in the updated history
        const totalImagesBefore = (Array.isArray(prevHistory) ? prevHistory : []).reduce((sum, entry) => sum + (Array.isArray(entry.images) ? entry.images.length : 0), 0);
        const lastBatchLength = Array.isArray(contentObject.images) ? contentObject.images.length : 0;
        const newSelectedIdx = lastBatchLength > 0 ? (totalImagesBefore + lastBatchLength - 1) : (totalImagesBefore > 0 ? totalImagesBefore - 1 : 0);
        sessionData.sessionSelectedImageIndex = newSelectedIdx;
        setGeneratedImgHistory(updatedHistory);
        setSessionSelectedImageIndex(newSelectedIdx);
      } catch (_) {
        // Non-fatal; proceed without history update
      }
      
      const success = safeSessionStorageSet('generatedContentData', sessionData);
      if (!success) {
        console.warn('Failed to save content to sessionStorage, but continuing with generation');
        toast.warning('Content generated but could not be saved locally. You may need to regenerate if you refresh the page.');
      }

      toast.success("Content generated successfully!");
    } catch (error) {
      // Check if the error is from aborting the request
      if (error.name === 'AbortError') {
        // Don't show error toast for user-initiated cancellation
        return;
      }
      
      console.error("Error generating content:", error);
      
      // Check if this might be a mobile browser backgrounding issue
      const isMobileBrowser = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isNetworkError = error.message.includes('Failed to fetch') || 
                            error.message.includes('NetworkError') || 
                            error.message.includes('TypeError');
      
      if (isMobileBrowser && isNetworkError && !isPageVisible) {
        // Likely a mobile browser backgrounding issue
        console.log('Detected potential mobile browser backgrounding issue');
        setIsBackgroundGeneration(true);
        setGenerationJobId(`temp_${Date.now()}`); // Temporary ID for tracking
        toast.info("Generation continues in background. Return to this tab to see results.");
        setShowServerGenerationMessage(true);
        return; // Don't clear generation state
      }
      
      // For other errors, show error message and server generation info
      toast.error("Failed to generate content");
      setApiError(error.message || "Failed to generate content. Please try again.");
      setShowServerGenerationMessage(true);
    } finally {
      // Only reset generation state if not in background generation mode
      if (!isBackgroundGeneration) {
        // Reset both the ref and the state - this must ALWAYS execute
        isGeneratingContentRef.current = false;
        setIsGeneratingContent(false);
        abortControllerRef.current = null; // Clear the abort controller
        
        // Reset timer based on post type
        if (instagramPostType === "reel") {
          setTimeRemaining(120); // 2:00 for reels
        } else {
          setTimeRemaining(timeNeeded); // 0:20 for feed and story
        }
      }
    }
  };

// sdfsf
  // Updated handlePublish function to work with multiple selected images
  const handlePublish = () => {
    setBusinessDetailsError("");
    // Retrieve email from Clerk or fallback to sessionStorage
    let email = "";
    if (user?.primaryEmailAddress?.emailAddress) {
      email = user.primaryEmailAddress.emailAddress;
    } else {
      const storedUserDetails = sessionStorage.getItem("userDetails");
      if (storedUserDetails) {
        try {
          const userDetails = JSON.parse(storedUserDetails);
          email = userDetails.email;
        } catch (err) {
          console.error("Error parsing userDetails from sessionStorage", err);
        }
      }
    }

    if (!email) {
      toast.error("User email not available");
      setIsPublishing(false);
      return;
    }
    
    // Get the appropriate content based on post type
    const currentContent = instagramPostType === "reel" ? generatedVidContent : generatedImgContent;
    
    // Ensure that generated content exists and at least one image is selected
    if (!currentContent || !currentContent.images || currentContent.images.length === 0) {
      toast.error(`No ${instagramPostType === "reel" ? "video" : "image"} available to publish.`);
      setIsPublishing(false);
      return;
    }
    
    /* Video-only: do not require selection of specific images here
    if (selectedImages.length === 0) {
      toast.error("Please select at least one image to publish.");
      setIsPublishing(false);
      return;
    }
    */
    
    const width = 800;
    const height = 600;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    
    // Pass the selected image indices to the InstagramConnectPrompt
    window.open(
      `${window.location.origin}/InstagramConnectPrompt?email=${encodeURIComponent(email)}&timestamp=${encodeURIComponent(contentTimestamp)}&selectedImages=${encodeURIComponent(JSON.stringify(selectedImages))}`,
      "InstagramConnect",
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no`
    );
    setIsPublishing(false);
  };

  // Toggle image selection
  const handleImageSelect = (index) => {
    setSelectedImages(prevSelected => {
      if (prevSelected.includes(index)) {
        return prevSelected.filter(i => i !== index);
      } else {
        return [...prevSelected, index];
      }
    });
    // Also update the selected image index for backward compatibility
    setSelectedImageIndex(index);
  };

  // Updated function to handle scheduling with multiple images
  const handleScheduleLater = () => {
    if (!userDetails?.hasBusinessDetails) {
      setBusinessDetailsError("Please fill in your business details in the Settings page before scheduling content.");
      toast.error("Please fill in your business details in the Settings page before scheduling content.");
      return;
    }
    setBusinessDetailsError("");

    // Get the appropriate content based on post type
    const currentContent = instagramPostType === "reel" ? generatedVidContent : generatedImgContent;
    
    if (contentGenerated && currentContent) {
      // Get caption from edited content or generated content
      const caption = isEditingContent ? editedContent : currentContent.content;
      
      /* Video-only: do not require explicit selection for scheduling; use all outputs by default
      // Check if any images are selected
      if (selectedImages.length === 0) {
        toast.error(`Please select at least one ${instagramPostType === "reel" ? "video" : "image"} to schedule.`);
        return;
      }
      */
      
      // Get URLs of selected or all media (video)
      const selectedMediaUrls = (selectedImages.length > 0 ? selectedImages : currentContent.images.map((_, i) => i))
        .map(index => currentContent.images[index].url);
      
      // Retrieve email from Clerk or fallback to sessionStorage
      let email = user?.primaryEmailAddress?.emailAddress || "";
      if (!email) {
        const storedUserDetails = sessionStorage.getItem("userDetails");
        if (storedUserDetails) {
          try {
            const userDetails = JSON.parse(storedUserDetails);
            email = userDetails.email || "";
          } catch (err) {
            console.error("Error parsing userDetails from sessionStorage", err);
          }
        }
      }
      
      if (!email) {
        toast.error("User email not available");
        return;
      }
      
      // Store the data in sessionStorage to access it on the schedule page
      const scheduleData = {
        caption,
        mediaUrls: selectedMediaUrls,
        email: email
      };
      // Use namespaced key for scheduleData
      const success = setSessionData('scheduleData', scheduleData, PAGE_TYPE);
      if (!success) {
        console.warn('Failed to save schedule data to sessionStorage');
        toast.warning('Could not save schedule data locally. Please try again.');
        return;
      }
      
      // Open the schedule page in a modal window
      const width = 800;
      const height = 600;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      
      // Use the current origin instead of API URL
      const frontendUrl = window.location.origin;
      
      // Pass the same parameters as InstagramConnectPrompt for consistency
      window.open(
        `${frontendUrl}/instagram-schedule?email=${encodeURIComponent(email)}&timestamp=${encodeURIComponent(contentTimestamp)}&selectedImages=${encodeURIComponent(JSON.stringify(selectedImages))}`,
        'InstagramSchedule',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no`
      );
    } else {
      toast.error("Please generate content first before scheduling");
    }
  };

  // listen for messages from the popup window
  useEffect(() => {
    const handleMessage = (event) => {
      // Check if the message is from our Instagram Schedule window
      if (event.data && event.data.type === 'INSTAGRAM_SCHEDULE') {
        if (event.data.status === 'success') {
          toast.success(event.data.message || 'Post scheduled successfully!');
        } else if (event.data.status === 'error') {
          toast.error(event.data.message || 'Failed to schedule post');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Clean up the event listener when component unmounts
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Handler for content mode change
  const handleContentModeChange = (mode) => {
    setContentMode(mode);
    // Clear the unused input when switching modes.
    // For "write", clear the product selection.
    // For "choose", clear the free text message.
    if (mode === "write") {
      setSelectedProduct("");
    } else if (mode === "choose") {
      setMessage("");
    } else if (mode === "upload") {
      // For "upload", clear both
      setSelectedProduct("");
      setMessage("");
    }
  };

  // Scroll to generated content when it becomes available
  useEffect(() => {
    const currentContent = instagramPostType === "reel" ? generatedVidContent : generatedImgContent;
    if (contentGenerated && currentContent && generatedContentRef.current) {
      // Smooth scroll to the generated content section
      generatedContentRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, [contentGenerated, generatedImgContent, generatedVidContent, instagramPostType]);

  // New function to stop generation without clearing form fields
  const handleStopGeneration = () => {
    // Abort any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Reset generation state ref to allow new requests
    isGeneratingContentRef.current = false;
    
    // Reset only generation-related states, keep form fields intact
    setIsGeneratingContent(false);
    setIsBackgroundGeneration(false);
    setGenerationJobId(null);
    setShowServerGenerationMessage(false);
    setApiError("");
    
    // Reset timer based on post type
    if (instagramPostType === "reel") {
      setTimeRemaining(360);
    } else {
      setTimeRemaining(20);
    }
    
    toast.success("Generation stopped");
  };

  // Manual retry function for background generation
  const handleRetryGeneration = async () => {
    if (isBackgroundGeneration) {
      console.log('Retrying generation after background issue...');
      setIsBackgroundGeneration(false);
      setGenerationJobId(null);
      // Trigger a new generation
      await handleGenerateContent();
    }
  };

  // Updated handler for clearing all content and selections
  const handleClearContent = () => {
    
    // Abort any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Reset generation state ref to allow new requests
    isGeneratingContentRef.current = false;
    
    // Store current values to preserve them
    const currentInstagramPostType = instagramPostType;
    const currentAspectRatio = aspectRatio;
    const currentMessage = message;
    const currentTextboxImages = [...textboxUploadedImages]; // Create a copy to preserve reference images
    
    // Clear from sessionStorage (use namespaced key)
    removeSessionData('generatedContentData', PAGE_TYPE);
    
    // Reset all state variables to initial values
    setSelectedPlatform("");
    setInstagramPostType(currentInstagramPostType); // Preserve the instagramPostType
    setFeedContentSource("");
    setContentMode("");
    setMediaSource("");
    setSelectedProduct("");
    setSelectedEvent("");
    setMessage(currentMessage); // Preserve the message
    // Clear original uploaded images and revoke object URLs
    originalUploadedImages.forEach(img => {
      URL.revokeObjectURL(img.preview);
    });
    setOriginalUploadedImages([]);
    
    // Clear reference uploaded images and revoke object URLs
    referenceUploadedImages.forEach(img => {
      URL.revokeObjectURL(img.preview);
    });
    setReferenceUploadedImages([]);
    
    // Preserve textbox uploaded images (reference images)
    setTextboxUploadedImages(currentTextboxImages);
    
    setAspectRatio(currentAspectRatio); // Preserve the aspect ratio
    setGeneratedImgContent(null);
    setGeneratedVidContent(null);
    setContentGenerated(false);
    setGenerateContentLabel("Generate Content");
    setHasApiCredits(false);
    setCurrentImageIndex(0);
    setSelectedImages([]);
    setSelectedImageIndex(null);
    setIsPublishing(false);
    setPublishStatus({ type: null, message: "" });
    setIsGeneratingContent(false);
    setIsEditingContent(false);
    setEditedContent("");
    setMessageError("");
    setNewContentPlaceholder(
      "Describe in as much details what would you like to see in the final image / video"
    );
    setUploadError("");
    setReferenceUploadError("");
    setOwnImageUploadError("");
    setTextboxUploadError(""); // Clear textbox upload error
    setContentTimestamp("");
    setApiError("");
    setIsAspectRatioExpanded(false);
    setShowServerGenerationMessage(false);
    setCreditInfo((prev) => ({ ...prev }));
    setGeneratedImgHistory([]);
    setSessionSelectedImageIndex(null);
    
    // If there's a file input with a value, clear it
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    
    // Clear textbox file input
    if (textboxFileInputRef.current) {
      textboxFileInputRef.current.value = "";
    }
    
    toast.success("All content and selections have been cleared");
  };

  // Open lightbox with the selected image index
  const openLightbox = useCallback((event, { photo, index }) => {
    setCurrentLightboxImage(index);
    setViewerIsOpen(true);
  }, []);

  // Close the lightbox modal
  const closeLightbox = () => {
    setViewerIsOpen(false);
  };

  // Revised Custom Image component for Gallery with lightbox support.
  // This component now applies layout props (such as margin, top, left, and direction)
  // that are computed by the react-photo-gallery library.
  const ImageComponent = ({ index, photo, margin, direction, top, left, onClick }) => {
    // Build container style using the computed margin, top, left, etc.
    const containerStyle = { margin, position: "relative" };
    if (direction === "column") {
      containerStyle.position = "absolute";
      containerStyle.left = left;
      containerStyle.top = top;
    }

    return (
      <div style={containerStyle} className={`image-container ${selectedImages.includes(index) ? "selected" : ""}`}>
        <img
          src={photo.src}
          alt={`Generated content ${index + 1}`}
          className="grid-image"
          style={{ 
            width: "100%",
            height: "auto",
            display: "block",
            objectFit: "scale-down",
            borderRadius: "12px",
            transition: "transform 0.3s ease-in-out"
          }}
          onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"}
          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
          onClick={(event) => onClick(event, { photo, index })}
        />
        <div className="image-checkbox">
          <input
            type="checkbox"
            id={`image-${index}`}
            checked={selectedImages.includes(index)}
            onChange={() => handleImageSelect(index)}
          />
          <label htmlFor={`image-${index}`} className="checkbox-label">
            {selectedImages.includes(index) && <span className="checkmark">✓</span>}
          </label>
        </div>
      </div>
    );
  };

  // Function to handle image click from Gallery component
  const imageClickHandler = (index) => {
    handleImageSelect(index);
  };

  // Handler to update the media source.
  // This function sets the new media source, clears any previous upload errors,
  // resets the file input value (if available), and cleans up uploaded images relevant to the previous media source.
  const handleMediaSourceChange = (newSource) => {
    setMediaSource(newSource);
    setUploadError("");

    // Clear the file input value to remove previous file selections.
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // Reset uploaded images based on the selected media source.
    // When switching to the AI magic image mode, clear both uploaded image arrays.
    if (newSource === "generate") {
      setOriginalUploadedImages([]);
      setReferenceUploadedImages([]);
    } else if (newSource === "original") {
      // When using your own photo, clear any reference uploads.
      setReferenceUploadedImages([]);
    } else if (newSource === "reference") {
      // When using a reference image, clear any original uploads.
      setOriginalUploadedImages([]);
    }
  };
  
   // Handle button enabling/disabling logic
  useEffect(() => {
    // Condition 1: Instagram feed/story with own image
    const condition1 = (instagramPostType === "feed" || instagramPostType === "story") && 
                       feedContentSource === "own_image";
    
    // Condition 2: Valid content mode selected
    const condition2 = contentMode === "write" || 
                       contentMode === "choose" || 
                       contentMode === "upload";
    
    // Condition 3: For reels, ensure duration is selected
    // const condition3 = instagramPostType === "reel" && reelDuration;

    // Condition 4: AI magic has completed and generated a message
    const condition4 = !isAiMagicLoading && message.trim().length > 0;
    
    // Enable button if either condition1/condition2/condition4 is met AND if it's a reel, condition3 must also be met
    setEnableGenerateContentButton((condition1 || condition2 || condition4));
  }, [instagramPostType, feedContentSource, contentMode, reelDuration, isAiMagicLoading, message]);

  // Add refs for each section that we want to scroll to
  // const step2Ref = React.useRef(null);
  // const step3Ref = React.useRef(null);

  // Mobile browser background handling states
  const [isPageVisible, setIsPageVisible] = useState(true);
  const [generationStartTime, setGenerationStartTime] = useState(null);
  const [isBackgroundGeneration, setIsBackgroundGeneration] = useState(false);
  const [generationJobId, setGenerationJobId] = useState(null);
  const [showServerGenerationMessage, setShowServerGenerationMessage] = useState(false);

  // Page Visibility API to detect when browser goes to background/foreground
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      setIsPageVisible(isVisible);
      
      // If page becomes visible and we were generating content in background
      if (isVisible && isBackgroundGeneration && generationJobId) {
        console.log('Page became visible, checking generation status...');
        checkGenerationStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isBackgroundGeneration, generationJobId]);

  // Check generation status when returning from background
  const checkGenerationStatus = async () => {
    if (!generationJobId) return;
    
    try {
      const token = await getToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/check_generation_status/${generationJobId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'completed' && data.result) {
          // Generation completed successfully
          handleGenerationSuccess(data.result);
          setIsBackgroundGeneration(false);
          setGenerationJobId(null);
        } else if (data.status === 'failed') {
          // Generation failed
          throw new Error(data.error || 'Generation failed');
        }
        // If still processing, continue showing loading state
      }
    } catch (error) {
      console.error('Error checking generation status:', error);
      // If we can't check status, show error but don't clear the generation state
      // The user can manually retry
    }
  };

  // Polling mechanism for background generation
  useEffect(() => {
    let pollInterval;
    
    if (isBackgroundGeneration && isPageVisible && generationJobId) {
      // Poll every 5 seconds when page is visible and generation is in background
      pollInterval = setInterval(() => {
        checkGenerationStatus();
      }, 5000);
    }
    
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [isBackgroundGeneration, isPageVisible, generationJobId]);

  // Show server generation message after 30 seconds if still generating
  useEffect(() => {
    let timeoutId;
    
    if (isGeneratingContent && !isBackgroundGeneration) {
      timeoutId = setTimeout(() => {
        setShowServerGenerationMessage(true);
      }, 30000); // 30 seconds
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isGeneratingContent, isBackgroundGeneration]);

  // Handle successful generation result
  const handleGenerationSuccess = (result) => {
    const { imageUrls, content, timestamp, credit } = result;
    
    // Process the result similar to the main generation flow
    const imageUrlArray = Object.entries(imageUrls).map(([prompt, urls]) => {
      const urlArray = Array.isArray(urls) ? urls : [urls];
      return urlArray.map((url, index) => ({
        prompt: `${prompt}_${index}`,
        url,
        isVideo: url.toLowerCase().endsWith('.mp4'),
        contentType: url.toLowerCase().endsWith('.mp4') ? 'vid' : 'img',
        index: index
      }));
    }).flat();

    const contentObject = {
      images: imageUrlArray,
      content: content,
    };

    if (instagramPostType === "reel") {
      setGeneratedVidContent(contentObject);
    } else {
      setGeneratedImgContent(contentObject);
    }
    
    setContentGenerated(true);
    setGenerateContentLabel("Regenerate");
    setSelectedImages([0]);
    setSelectedImageIndex(0);
    setContentTimestamp(timestamp);
    
    if (credit) {
      refreshHeaderCredits(credit);
      setHasApiCredits(true);
    }
    
    // Clear server generation message on success
    setShowServerGenerationMessage(false);
    toast.success("Content generated successfully!");
  };

  // Scroll to ContentSkeleton when content generation starts
  useEffect(() => {
    if (isGeneratingContent) {
      // Add a delay to ensure the component is rendered before scrolling
      const scrollTimeout = setTimeout(() => {
        // Scroll to the generated content section instead
        if (generatedContentRef.current) {
          generatedContentRef.current.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 20); // Increased delay to ensure component is fully rendered
      
      // Cleanup timeout if component unmounts or isGeneratingContent changes
      return () => clearTimeout(scrollTimeout);
    }
  }, [isGeneratingContent]);

  // Add this handler for the settings button click
  const handleSettingsClick = () => {
    setShowAdvancedSettings(true);
  };

  // Add this handler for aspect ratio change
  const handleAspectRatioChange = (newRatio) => {
    setAspectRatio(newRatio);
    setShowAdvancedSettings(false);
  };
  
  // Add handler for style change
  const handleStyleChange = (styleId) => {
    setSelectedStyle(styleId);
    setShowAdvancedSettings(false);
  };


  // Function to get aspect ratio display name
  const getAspectRatioName = (ratio) => {
    const aspectRatioOptions = {
      'Square': 'Square',
      'Portrait': 'Portrait', 
      'Landscape': 'Landscape'
    };
    return aspectRatioOptions[ratio] || '';
  };

  // Cleanup blob URLs on component unmount
  useEffect(() => {
    return () => {
      textboxUploadedImages.forEach(img => {
        if (img.preview) {
          URL.revokeObjectURL(img.preview);
        }
      });
    };
  }, []);
  
  // Add: helper to detect low credits error and handler to buy credits
  const isLowCreditsError = (errMsg) => {
    try {
      return typeof errMsg === 'string' && errMsg.toLowerCase().includes('low credit');
    } catch (_) {
      return false;
    }
  };

  
  // Dismiss error popups when clicking anywhere outside them
  useEffect(() => {
    const handleGlobalClick = (event) => {
      // If no error is visible, do nothing
      const anyErrorVisible = Boolean(
        messageError ||
        textboxUploadError ||
        apiError
      );
      if (!anyErrorVisible) return;

      // If click is outside the error popup container, clear errors
      if (errorPopupRef.current && !errorPopupRef.current.contains(event.target)) {
        setMessageError("");
        setTextboxUploadError("");
        setApiError("");
      }
    };

    document.addEventListener('mousedown', handleGlobalClick);
    return () => document.removeEventListener('mousedown', handleGlobalClick);
  }, [messageError, textboxUploadError, apiError]);
  
  return (
    <div className="min-h-screen flex flex-col justify-start items-center relative bg-gradient-to-br from-black via-gray-900 to-black overflow-y-auto">
      {/* Header */}
      <CreateContentHeader creditInfo={creditInfo} hasApiCredits={hasApiCredits} onHelpClick={() => setShowTour(true)} />
      
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-10 sm:top-16 md:top-20 left-4 sm:left-10 md:left-20 w-32 sm:w-48 md:w-64 h-32 sm:h-48 md:h-64 bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-full blur-2xl sm:blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-10 sm:bottom-16 md:bottom-20 right-4 sm:right-10 md:right-20 w-48 sm:w-72 md:w-96 h-48 sm:h-72 md:h-96 bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-full blur-2xl sm:blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Main content with consistent spacing */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col pt-12 pb-8 sm:pb-12 md:pb-16">
        
        {/* Workspace Layout */}
        <div className={`flex gap-6 ${isSidebarCollapsed ? 'lg:pl-28' : 'lg:pl-80'} transition-all duration-300`}>
          
          {/* Generated Content Area */}
          <div className="flex-1 w-full flex flex-col">
            
            {/* Generated Content Section - Centered and Responsive */}
            <div className="flex-1 flex flex-col items-center justify-start min-h-0">
              
              {/* Generated Content Section - Centered */}
              <div className="w-full flex justify-center">
                <GeneratedVideo
                  isGeneratingContent={isGeneratingContent}
                  contentGenerated={contentGenerated}
                  generatedImgContent={generatedImgContent}
                  generatedVidContent={generatedVidContent}
                  instagramPostType={instagramPostType}
                  timeRemaining={timeRemaining}
                  selectedImages={selectedImages}
                  isEditingContent={isEditingContent}
                  editedContent={editedContent}
                  setEditedContent={setEditedContent}
                  isPlaying={isPlaying}
                  setIsPlaying={setIsPlaying}
                  isPublishing={isPublishing}
                  publishStatus={publishStatus}
                  contentTimestamp={contentTimestamp}
                  viewerIsOpen={viewerIsOpen}
                  currentLightboxImage={currentLightboxImage}
                  creditInfo={creditInfo}
                  user={user}
                  handleStopGeneration={handleStopGeneration}
                  handleToggleEdit={handleToggleEdit}
                  handlePublish={handlePublish}
                  handleScheduleLater={handleScheduleLater}
                  openLightbox={openLightbox}
                  closeLightbox={closeLightbox}
                  handleClearContent={handleClearContent}
                  // New props for session-wide carousel
                  generatedImgHistory={generatedImgHistory}
                  sessionSelectedImageIndex={sessionSelectedImageIndex}
                  setSessionSelectedImageIndex={setSessionSelectedImageIndex}
                />
              {/* </div> */}

              {/* Content Skeleton - Centered when generating */}
              {(isGeneratingContent || isBackgroundGeneration) && (
                <div className="fixed inset-0 z-40 flex items-center justify-center pointer-events-none">
                  <ContentSkeleton 
                    timeRemaining={timeRemaining}
                    totalTime={totalTime}
                    onExit={handleStopGeneration}
                    isBackgroundGeneration={isBackgroundGeneration}
                    isPageVisible={isPageVisible}
                    onRetry={handleRetryGeneration}
                  />
                </div>
              )}
              </div>

              {/* Error Messages - Centered */}
              <div className="w-full max-w-4xl mx-auto mt-6 fixed inset-0 z-[60] flex items-center justify-center pointer-events-none">
                <div ref={errorPopupRef} className="pointer-events-auto">
                {messageError && (
                  <motion.div 
                    className="p-3 glass-effect border border-red-500/30 rounded-lg flex items-start gap-2"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FiAlertCircle className="text-red-400 mt-0.5" />
                    <div className="flex flex-col">
                      <span className="text-red-400 text-sm">{messageError}</span>
                      {isLowCreditsError(messageError) && (
                        <div className="mt-2">
                          <button
                            onClick={() => navigate('/billing')}
                            className="px-3 py-1.5 text-xs sm:text-sm rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold transition-colors duration-300"
                          >
                            Click to buy credits
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {textboxUploadError && (
                  <motion.div 
                    className="p-3 glass-effect border border-red-500/30 rounded-lg flex items-center gap-2"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FiAlertCircle className="text-red-400" />
                    <span className="text-red-400 text-sm">{textboxUploadError}</span>
                  </motion.div>
                )}

                {apiError && (
                  <div className="mt-4">
                    <ErrorAlert message={apiError} />
                    {isLowCreditsError(apiError) && (
                      <div className="mt-2 flex justify-center">
                        <button
                          onClick={() => navigate('/billing')}
                          className="px-3 py-1.5 text-sm rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold transition-colors duration-300"
                        >
                          Click to buy credits
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {showServerGenerationMessage && (
                  <motion.div 
                    className="p-4 glass-effect border border-blue-500/30 rounded-lg flex items-start gap-3 pointer-events-auto max-w-md"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-blue-400 font-medium text-sm mb-2">Generation in Progress</h4>
                      <p className="text-gray-300 text-sm mb-3">
                        Don't worry! Your video is still being generated on our servers. 
                        It will be available in your Dashboard once complete.
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setShowServerGenerationMessage(false);
                            navigate('/dashboard');
                          }}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-300 hover:text-blue-200 transition-colors duration-300"
                        >
                          Go to Dashboard
                        </button>
                        <button
                          onClick={() => setShowServerGenerationMessage(false)}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-500/20 hover:bg-gray-500/30 border border-gray-500/30 text-gray-300 hover:text-gray-200 transition-colors duration-300"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
              </div>

            </div>
          </div>

          {/* Chatbox */}
          <CreateVideoEditor
            instagramPostType={instagramPostType}
            setInstagramPostType={setInstagramPostType}
            creationInputMode={creationInputMode}
            setCreationInputMode={setCreationInputMode}
            selectedCreationOption={selectedCreationOption}
            setSelectedCreationOption={setSelectedCreationOption}
            message={message}
            setMessage={setMessage}
            messageError={messageError}
            textboxUploadedImages={textboxUploadedImages}
            setTextboxUploadedImages={setTextboxUploadedImages}
            textboxUploadError={textboxUploadError}
            selectedModel={selectedModel}
            setSelectedModel={setSelectedModel}
            isModelCollapsed={isModelCollapsed}
            setIsModelCollapsed={setIsModelCollapsed}
            isOutputCollapsed={isOutputCollapsed}
            setIsOutputCollapsed={setIsOutputCollapsed}
            aspectRatio={aspectRatio}
            setAspectRatio={setAspectRatio}
            reelDuration={reelDuration}
            setReelDuration={setReelDuration}
            reelDurationError={reelDurationError}
            resolution={resolution}
            setResolution={setResolution}
            mode={mode}
            setMode={setMode}
            imageResolution={imageResolution}
            setImageResolution={setImageResolution}
            outputSamples={outputSamples}
            setOutputSamples={setOutputSamples}
            isAudioCollapsed={isAudioCollapsed}
            setIsAudioCollapsed={setIsAudioCollapsed}
            audioOption={audioOption}
            setAudioOption={setAudioOption}
            audioText={audioText}
            setAudioText={setAudioText}
            isGeneratingContent={isGeneratingContent}
            generateContentLabel={generateContentLabel}
            isSidebarCollapsed={isSidebarCollapsed}
            setIsSidebarCollapsed={setIsSidebarCollapsed}
            isImageUploading={isImageUploading}
            // Event handlers
            handleTextboxFileUpload={handleTextboxFileUpload}
            removeTextboxImage={removeTextboxImage}
            handleStopGeneration={handleStopGeneration}
            handleGenerateContent={handleGenerateContent}
            // Refs
            messageTextareaRef={messageTextareaRef}
            textboxFileInputRef={textboxFileInputRef}
            // New dresses props
// Event props
            selectedEvent={selectedEvent}
            setSelectedEvent={setSelectedEvent}
            selectedDresses={selectedDresses}
            setSelectedDresses={setSelectedDresses}
            selectedHumanModel={selectedHumanModel}
            setSelectedHumanModel={setSelectedHumanModel}
            // Product props
            productType={productType}
            setProductType={setProductType}
            productItem={productItem}
            setProductItem={setProductItem}
            // Style props
            selectedStyle={selectedStyle}
            setSelectedStyle={setSelectedStyle}
            realLooks={realLooks}
            setRealLooks={setRealLooks}
            setShowTour={setShowTour}
            isCenteredPosition={!contentGenerated && !isGeneratingContent && !apiError && !messageError && !textboxUploadError}
            disable1080p={gates.disable1080p}
            disable720p={gates.disable720p}
            disablePremium={gates.disablePremium}
            // AI Model props
            selectedAIModel={selectedAIModel}
            setSelectedAIModel={setSelectedAIModel}
          />

        </div>
      </div>
      
      {/* First-time Help Tour */}
      <FirstTimeUserTour isOpen={showTour} onClose={() => setShowTour(false)} />
    </div>
  );
}

export default CreateVideo; 